import { useMemo } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useAccounts } from "@hooks/useAccounts";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useHasAccrualLedger, useLedgers } from "@api/ledger.api";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import { MatchableTransaction } from "../types";

export function useAllocationLedgerContext(
    sourceTransaction: MatchableTransaction | null,
) {
    const entityId = useEntityIdForGLAccount(sourceTransaction ?? undefined);
    const hasAccrualLedger = useHasAccrualLedger(entityId);
    const { data: ledgers } = useLedgers(entityId);
    const primaryAccrualLedgerId = useMemo(
        () => findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals),
        [ledgers],
    );
    const primaryCashLedgerId = useMemo(
        () => findPrimaryLedgerId(ledgers, AccountingLedgerBasis.cash),
        [ledgers],
    );
    const showAccountNumbers = useShowAccountNumbers();
    const { accounts } = useAccounts({
        entityId: hasAccrualLedger ? entityId : undefined,
        ledgerBasis: AccountingLedgerBasis.accruals,
    });

    return {
        entityId,
        hasAccrualLedger,
        primaryAccrualLedgerId,
        primaryCashLedgerId,
        showAccountNumbers,
        accounts,
    };
}

export type AllocationLedgerContext = ReturnType<
    typeof useAllocationLedgerContext
>;
